// ============================================================================
// mockData.js
// ----------------------------------------------------------------------------
// Mock data layer for the Space Coast Procurement Portal.
//
// This file isolates all "fake" data behind clean service-style functions so a
// future buyer can swap these out for live REST/GraphQL/MongoDB calls without
// touching any UI component. Each exported function below is intentionally
// shaped like an async API call (returning a Promise) — drop in `fetch()` /
// `axios.get()` calls in their place.
// ============================================================================

// --- Pre-seeded active bids (verbatim from product spec) ---------------------
export const ACTIVE_BIDS = [
  {
    id: "RFQ-4-26-09",
    title: "Minor Construction & Commercial Repairs",
    agency: "Brevard County BoCC",
    platform: "VendorLink",
    location: "Countywide / Melbourne",
    closing: "2026-06-15",
    type: "Small Business Set-Aside",
    value: "Under $200k",
  },
  {
    id: "33-ITB-26",
    title: "Pond & Drainage Improvements (Cadillac Dr & RJ Conlan)",
    agency: "City of Palm Bay",
    platform: "BonfireHub",
    location: "Palm Bay",
    closing: "2026-06-09",
    type: "Municipal ITB",
    value: "Mid-Tier",
  },
  {
    id: "RFP-2026-DIW",
    title: "Commercial Deep Injection Well Construction",
    agency: "City of West Melbourne",
    platform: "DemandStar",
    location: "Minton Road Complex",
    closing: "2026-06-11",
    type: "Full RFP",
    value: "High-Value",
  },
  {
    id: "B-2-26-34",
    title: "HVAC Commercial Replacement (Port St. John Center)",
    agency: "Brevard County BoCC",
    platform: "VendorLink",
    location: "Port St. John",
    closing: "2026-06-22",
    type: "Standard Commercial",
    value: "Under $100k",
  },
  {
    id: "B-2-26-38",
    title: "Commercial Demolition & Site Clearance",
    agency: "Brevard County BoCC",
    platform: "VendorLink",
    location: "Micco Area",
    closing: "2026-06-18",
    type: "Site Prep",
    value: "Mid-Tier",
  },
];

// --- Per-bid detail metadata shown in the slide-out drawer -------------------
// Keyed by bid id. Replace this with a `GET /api/bids/:id` endpoint later.
export const BID_DETAILS = {
  "RFQ-4-26-09": {
    scope:
      "Open continuing-services contract for minor construction trades and commercial repair work across county-owned facilities. Includes light demolition, drywall, fixture install, and small structural repairs not exceeding the single-project ceiling.",
    sites: ["Brevard Countywide Facilities", "Central Melbourne Operations Yard"],
    bond:
      "No payment/performance bond required under set-aside threshold. General Liability $1M / Workers' Comp required prior to award.",
  },
  "33-ITB-26": {
    scope:
      "Stormwater pond reshaping, embankment regrading, and conveyance pipe replacement at the Cadillac Dr. and RJ Conlan retention basins. Includes silt removal, riprap install, and post-construction stabilization.",
    sites: ["Cadillac Dr. Retention Basin, Palm Bay", "RJ Conlan Outfall, Palm Bay"],
    bond:
      "100% Payment & Performance Bond required at award. Bid Bond 5% required at submission. Builder's Risk policy mandatory.",
  },
  "RFP-2026-DIW": {
    scope:
      "Design-build construction of a Class I commercial deep injection well to serve the Minton Road wastewater complex. Scope includes geophysical logging, dual-zone monitoring, FDEP permitting support, and mechanical integrity testing.",
    sites: ["Minton Road Wastewater Complex, West Melbourne"],
    bond:
      "Full P&P Bond at 100% of contract value. Pollution Liability $5M aggregate. FDEP UIC contractor certification required.",
  },
  "B-2-26-34": {
    scope:
      "Removal and like-for-like replacement of rooftop HVAC units serving the Port St. John Community Center, including new curbs, controls integration with existing BAS, and refrigerant reclamation per EPA Section 608.",
    sites: ["Port St. John Community Center"],
    bond:
      "Payment & Performance Bond required for awards above $100k. Mechanical Contractor license (CMC) mandatory.",
  },
  "B-2-26-38": {
    scope:
      "Selective and full-structure demolition with site clearance of two county-acquired parcels in the Micco corridor. Includes asbestos survey coordination, debris haul-off, and final grade certification.",
    sites: ["Micco Parcel A — County Road 510", "Micco Parcel B — Babcock St. South"],
    bond:
      "Performance Bond at 100%. Asbestos abatement subcontractor must hold active FDBPR license.",
  },
};

// --- Pre-seeded "Saved Contracts" for the saved view -------------------------
export const SAVED_CONTRACTS = [
  {
    id: "RFP-2026-DIW",
    title: "Commercial Deep Injection Well Construction",
    agency: "City of West Melbourne",
    savedOn: "2026-02-04",
    note: "Pre-qualifying — coordinating with hydrogeology sub.",
  },
  {
    id: "33-ITB-26",
    title: "Pond & Drainage Improvements (Cadillac Dr & RJ Conlan)",
    agency: "City of Palm Bay",
    savedOn: "2026-02-06",
    note: "Site walk attended. Pricing earthwork now.",
  },
];

// --- Filter option constants used by dropdowns -------------------------------
export const AGENCY_OPTIONS = [
  "All Agencies",
  "Brevard County BoCC",
  "City of Palm Bay",
  "City of West Melbourne",
];

export const PLATFORM_OPTIONS = [
  "All Platforms",
  "VendorLink",
  "BonfireHub",
  "DemandStar",
];

// ============================================================================
// Service-style accessors — swap these for real API calls later.
// ============================================================================

export async function fetchActiveBids() {
  // TODO: replace with `await axios.get('/api/bids?status=active')`
  return ACTIVE_BIDS;
}

export async function fetchBidDetail(bidId) {
  // TODO: replace with `await axios.get('/api/bids/' + bidId)`
  return BID_DETAILS[bidId] || null;
}

export async function fetchSavedContracts() {
  // TODO: replace with `await axios.get('/api/contracts/saved')`
  return SAVED_CONTRACTS;
}
